import { Group, Loader, Tooltip } from "@mantine/core";
import { useState } from "react";
import {
  FiArchive,
  FiCode,
  FiExternalLink,
  FiFile,
  FiFileText,
  FiFilm,
  FiGrid,
  FiImage,
  FiMonitor,
  FiMusic,
  FiX,
} from "react-icons/fi";
import { getActionAttachmentDownloadUrl } from "api";
import { notify } from "utils/common";
import { formatAttachmentSize } from "utils/attachmentUploads";
import css from "./ActionAttachmentBadges.module.scss";

// Signed URLs live 10 minutes on the server. Reuse them for 9 so a second
// click on the same file opens at once, from any badge on the page.
const SIGNED_URL_REUSE_MS = 9 * 60 * 1000;
const signedUrlCache = new Map();

const getCachedSignedUrl = (storagePath) => {
  const cached = signedUrlCache.get(storagePath);
  if (!cached) return null;
  if (cached.expiresAt <= Date.now()) {
    signedUrlCache.delete(storagePath);
    return null;
  }
  return cached.url;
};

// Returns false when the browser blocks the tab. Safari does this when the
// open happens after an await instead of directly inside the click.
const openInNewTab = (url) => {
  const targetWindow = window.open(url, "_blank");
  if (!targetWindow) return false;
  targetWindow.opener = null;
  return true;
};

const getAttachmentIcon = (attachment) => {
  const contentType = String(attachment?.contentType || "").toLowerCase();
  const extension = String(attachment?.name || "")
    .split(".")
    .pop()
    .toLowerCase();

  if (contentType.startsWith("image/")) return FiImage;
  if (contentType.startsWith("video/")) return FiFilm;
  if (contentType.startsWith("audio/")) return FiMusic;
  if (contentType.includes("zip") || contentType.includes("compressed")) {
    return FiArchive;
  }

  if (["png", "jpg", "jpeg", "gif", "webp", "svg", "heic"].includes(extension)) {
    return FiImage;
  }
  if (["mp4", "mov", "avi", "mkv", "webm"].includes(extension)) return FiFilm;
  if (["mp3", "wav", "aac", "m4a", "flac"].includes(extension)) return FiMusic;
  if (["zip", "rar", "7z", "tar", "gz"].includes(extension)) return FiArchive;
  if (["xls", "xlsx", "csv", "numbers"].includes(extension)) return FiGrid;
  if (["ppt", "pptx", "key"].includes(extension)) return FiMonitor;
  if (
    [
      "js",
      "jsx",
      "ts",
      "tsx",
      "json",
      "html",
      "css",
      "scss",
      "xml",
      "yaml",
      "yml",
    ].includes(extension)
  ) {
    return FiCode;
  }
  if (["pdf", "doc", "docx", "txt", "md", "rtf"].includes(extension)) {
    return FiFileText;
  }

  return FiFile;
};

const ActionAttachmentBadges = ({
  attachments = [],
  actionId,
  actionOwnerUid,
  collectionName = "ActionItems",
  removable = false,
  onRemove,
}) => {
  const [openingId, setOpeningId] = useState(null);
  const [readyId, setReadyId] = useState(null);

  if (!attachments.length) return null;

  const getUploadProgressLabel = (attachment) => {
    if (!attachment?.uploading) return null;
    const progress = Number(attachment.progress);
    if (!Number.isFinite(progress) || progress <= 0) return "Uploading";
    return `${Math.min(100, Math.max(0, Math.round(progress)))}%`;
  };

  const openAttachment = async (attachment, attachmentKey) => {
    if (!attachment?.storagePath || removable || openingId) return;

    const cachedUrl = getCachedSignedUrl(attachment.storagePath);
    if (cachedUrl) {
      setReadyId(openInNewTab(cachedUrl) ? null : attachmentKey);
      return;
    }

    setReadyId(null);
    setOpeningId(attachmentKey);

    try {
      const response = await getActionAttachmentDownloadUrl({
        actionId,
        actionOwnerUid,
        collectionName,
        storagePath: attachment.storagePath,
      });

      if (!response?.url) {
        notify("Unable to open attachment", "error");
        return;
      }

      signedUrlCache.set(attachment.storagePath, {
        url: response.url,
        expiresAt: Date.now() + SIGNED_URL_REUSE_MS,
      });

      if (!openInNewTab(response.url)) setReadyId(attachmentKey);
    } catch (error) {
      console.error("Error opening action attachment:", error);
      notify(
        error?.code === "functions/deadline-exceeded"
          ? "The file took too long to open. Try again."
          : "Unable to open attachment",
        "error"
      );
    } finally {
      setOpeningId(null);
    }
  };

  return (
    <Group spacing={6} className={css.attachments}>
      {attachments.map((attachment, index) => {
        const attachmentKey =
          attachment.id || attachment.storagePath || `${attachment.name}-${index}`;
        const isOpening = openingId === attachmentKey;
        const isReady = readyId === attachmentKey;
        const canOpen = !removable && Boolean(attachment?.storagePath);
        const label = `${attachment.name || "Attachment"} (${formatAttachmentSize(
          attachment.size
        )})`;
        const Icon = getAttachmentIcon(attachment);
        const isUploading = Boolean(attachment?.uploading);
        const uploadProgressLabel = getUploadProgressLabel(attachment);
        const showSpinner = isUploading || isOpening;
        let fileNameLabel = attachment.name || "Attachment";
        if (isOpening) fileNameLabel = "Opening...";
        if (isReady) fileNameLabel = "Click to open";

        return (
          <Tooltip key={attachmentKey} label={label} width="auto">
            <span
              className={`${css.badge} ${canOpen ? css.clickable : ""} ${
                isUploading ? css.uploading : ""
              } ${isOpening ? css.opening : ""} ${isReady ? css.ready : ""}`}
              onClick={() => openAttachment(attachment, attachmentKey)}
              aria-busy={isOpening || undefined}
              role={canOpen ? "button" : undefined}
              tabIndex={canOpen ? 0 : undefined}
              onKeyDown={(event) => {
                if (!canOpen) return;
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  openAttachment(attachment, attachmentKey);
                }
              }}
              title={isUploading ? "Uploading..." : isOpening ? "Opening..." : label}
            >
              {showSpinner && (
                <Loader className={css.uploadSpinner} size={14} color="gray" />
              )}
              {!showSpinner && isReady && (
                <FiExternalLink className={css.fileIcon} size={14} aria-hidden="true" />
              )}
              {!showSpinner && !isReady && (
                <Icon className={css.fileIcon} size={14} aria-hidden="true" />
              )}
              <span className={css.fileName}>{fileNameLabel}</span>
              {uploadProgressLabel && (
                <span className={css.uploadProgress}>{uploadProgressLabel}</span>
              )}
              {removable && !isUploading && (
                <button
                  type="button"
                  className={css.removeButton}
                  aria-label={`Remove ${attachment.name || "attachment"}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    onRemove?.(attachment);
                  }}
                >
                  <FiX size={13} />
                </button>
              )}
            </span>
          </Tooltip>
        );
      })}
    </Group>
  );
};

export default ActionAttachmentBadges;
